import { z } from 'zod';
export * from './native-releases.js';
export * from './browser-session.js';
const serverEnvironmentSchema = z.object({
    NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
    HOST: z.string().trim().min(1).default('127.0.0.1'),
    PORT: z.coerce.number().int().min(1).max(65_535).default(3000),
    LOG_LEVEL: z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace']).default('info'),
    DATABASE_URL: z
        .url()
        .refine((value) => value.startsWith('postgres://') || value.startsWith('postgresql://'), {
        message: 'DATABASE_URL must use the postgres or postgresql scheme',
    }),
    CONSOLE_ORIGIN: z.url().default('http://127.0.0.1:5173'),
    ATUM_AUTH_ISSUER: z.url().default('https://auth.atum.tech/api/auth'),
    ATUM_AUTH_CLIENT_ID: z.string().trim().min(1).optional(),
    ATUM_AUTH_NATIVE_CLIENT_ID: z.preprocess((value) => (value === '' ? undefined : value), z.literal('atum-ios-remote').optional()),
    ATUM_AUTH_IOS_APP_CLIENT_ID: z.preprocess((value) => (value === '' ? undefined : value), z.literal('atum-ios-app').optional()),
    ATUM_AUTH_CLIENT_SECRET: z.string().min(1).optional(),
    ATUM_AUTH_OWNER_SUBJECT: z.string().trim().min(1).optional(),
    ATUM_AUTH_NYX_ENABLED: z.enum(['true', 'false']).default('false'),
    ATUM_AUTH_CLIPBOARD_ENABLED: z.enum(['true', 'false']).default('false'),
    ATUM_AUTH_PERSONAL_ACCOUNTS_ENABLED: z.enum(['true', 'false']).default('false'),
    ATUM_AUTH_FILES_ENABLED: z.enum(['true', 'false']).default('false'),
    ATUM_AUTH_LINUX_PRIVILEGES_ENABLED: z.enum(['true', 'false']).default('false'),
    ATUM_AUTH_APPROVAL_ENDPOINT: z.url().optional(),
    ATUM_AUTH_APPROVAL_SERVICE_SECRET: z.string().min(32).optional(),
    METRICS_SCRAPE_TOKEN: z
        .string()
        .regex(/^[A-Za-z0-9_-]{43}$/)
        .optional(),
    AUTHENTICATION_RATE_LIMIT_MAX: z.coerce.number().int().min(1).max(1000).default(10),
    AUTHENTICATION_RATE_LIMIT_WINDOW_MS: z.coerce
        .number()
        .int()
        .min(1000)
        .max(3_600_000)
        .default(60_000),
    ENROLLMENT_RATE_LIMIT_MAX: z.coerce.number().int().min(1).max(1000).default(5),
    ENROLLMENT_RATE_LIMIT_WINDOW_MS: z.coerce
        .number()
        .int()
        .min(1000)
        .max(3_600_000)
        .default(300_000),
    REQUIRE_TLS: z.enum(['true', 'false']).optional(),
    TRUST_PROXY: z.enum(['true', 'false']).default('false'),
    WEBSOCKET_HEARTBEAT_MS: z.coerce.number().int().min(5_000).max(300_000).default(30_000),
});
const databaseEnvironmentSchema = z.object({
    DATABASE_URL: z
        .url()
        .refine((value) => value.startsWith('postgres://') || value.startsWith('postgresql://'), {
        message: 'DATABASE_URL must use the postgres or postgresql scheme',
    }),
});
export function parseServerEnvironment(environment) {
    const parsed = serverEnvironmentSchema.parse(environment);
    let browserAuth;
    if (parsed.ATUM_AUTH_CLIENT_ID ||
        parsed.ATUM_AUTH_NATIVE_CLIENT_ID ||
        parsed.ATUM_AUTH_IOS_APP_CLIENT_ID ||
        parsed.ATUM_AUTH_CLIENT_SECRET ||
        parsed.ATUM_AUTH_OWNER_SUBJECT) {
        if (!parsed.ATUM_AUTH_CLIENT_ID ||
            !parsed.ATUM_AUTH_CLIENT_SECRET ||
            !parsed.ATUM_AUTH_OWNER_SUBJECT) {
            throw new Error('ATUM Auth requires a client ID, client secret, and exact owner subject.');
        }
        const issuer = new URL(parsed.ATUM_AUTH_ISSUER);
        const origin = new URL(parsed.CONSOLE_ORIGIN);
        if (issuer.protocol !== 'https:' ||
            issuer.username ||
            issuer.password ||
            issuer.search ||
            issuer.hash ||
            origin.username ||
            origin.password ||
            origin.pathname !== '/' ||
            origin.search ||
            origin.hash ||
            (origin.protocol !== 'https:' &&
                !(parsed.NODE_ENV !== 'production' &&
                    origin.protocol === 'http:' &&
                    ['127.0.0.1', 'localhost', '[::1]'].includes(origin.hostname)))) {
            throw new Error('ATUM Auth requires an HTTPS issuer and console origin (loopback HTTP is development-only).');
        }
        browserAuth = {
            issuer: issuer.href.replace(/\/$/, ''),
            clientId: parsed.ATUM_AUTH_CLIENT_ID,
            ...(parsed.ATUM_AUTH_NATIVE_CLIENT_ID
                ? { nativeClientId: parsed.ATUM_AUTH_NATIVE_CLIENT_ID }
                : {}),
            ...(parsed.ATUM_AUTH_IOS_APP_CLIENT_ID
                ? { iosAppClientId: parsed.ATUM_AUTH_IOS_APP_CLIENT_ID }
                : {}),
            clientSecret: parsed.ATUM_AUTH_CLIENT_SECRET,
            ownerSubject: parsed.ATUM_AUTH_OWNER_SUBJECT,
            nyxEnabled: parsed.ATUM_AUTH_NYX_ENABLED === 'true',
            clipboardEnabled: parsed.ATUM_AUTH_CLIPBOARD_ENABLED === 'true',
            personalAccountsEnabled: parsed.ATUM_AUTH_PERSONAL_ACCOUNTS_ENABLED === 'true',
            filesEnabled: parsed.ATUM_AUTH_FILES_ENABLED === 'true',
            linuxPrivilegesEnabled: parsed.ATUM_AUTH_LINUX_PRIVILEGES_ENABLED === 'true',
        };
    }
    let linuxPrivileges;
    if (parsed.ATUM_AUTH_LINUX_PRIVILEGES_ENABLED === 'true') {
        if (!browserAuth ||
            !parsed.ATUM_AUTH_APPROVAL_ENDPOINT ||
            !parsed.ATUM_AUTH_APPROVAL_SERVICE_SECRET)
            throw new Error('Linux privileges require browser identity and the private Auth approval channel.');
        const subject = browserAuth.ownerSubject;
        if (subject !== environment.ATUM_AUTH_OWNER_SUBJECT ||
            new TextEncoder().encode(subject).length > 128 ||
            [...subject].some((char) => char.charCodeAt(0) < 32 || (char.charCodeAt(0) >= 127 && char.charCodeAt(0) <= 159)))
            throw new Error('Linux privileges require an exact owner subject of at most 128 UTF-8 bytes without control characters.');
        const endpoint = new URL(parsed.ATUM_AUTH_APPROVAL_ENDPOINT);
        if (endpoint.username ||
            endpoint.password ||
            endpoint.search ||
            endpoint.hash ||
            endpoint.pathname !== '/' ||
            (endpoint.protocol !== 'https:' &&
                !(endpoint.protocol === 'http:' && ['127.0.0.1', '[::1]'].includes(endpoint.hostname))))
            throw new Error('Approval channel requires HTTPS or a protected loopback tunnel.');
        linuxPrivileges = {
            endpoint: endpoint.origin,
            secret: parsed.ATUM_AUTH_APPROVAL_SERVICE_SECRET,
        };
    }
    return {
        ...(linuxPrivileges ? { linuxPrivileges } : {}),
        ...(browserAuth ? { browserAuth } : {}),
        nodeEnv: parsed.NODE_ENV,
        host: parsed.HOST,
        port: parsed.PORT,
        logLevel: parsed.LOG_LEVEL,
        databaseUrl: parsed.DATABASE_URL,
        consoleOrigin: parsed.CONSOLE_ORIGIN,
        ...(parsed.METRICS_SCRAPE_TOKEN === undefined
            ? {}
            : { metricsScrapeToken: parsed.METRICS_SCRAPE_TOKEN }),
        authenticationRateLimitMax: parsed.AUTHENTICATION_RATE_LIMIT_MAX,
        authenticationRateLimitWindowMs: parsed.AUTHENTICATION_RATE_LIMIT_WINDOW_MS,
        enrollmentRateLimitMax: parsed.ENROLLMENT_RATE_LIMIT_MAX,
        enrollmentRateLimitWindowMs: parsed.ENROLLMENT_RATE_LIMIT_WINDOW_MS,
        requireTls: parsed.REQUIRE_TLS === undefined
            ? parsed.NODE_ENV === 'production'
            : parsed.REQUIRE_TLS === 'true',
        trustProxy: parsed.TRUST_PROXY === 'true',
        websocketHeartbeatMs: parsed.WEBSOCKET_HEARTBEAT_MS,
    };
}
export function parseDatabaseEnvironment(environment) {
    const parsed = databaseEnvironmentSchema.parse(environment);
    return { databaseUrl: parsed.DATABASE_URL };
}
const consoleEnvironmentSchema = z.object({
    VITE_API_BASE_URL: z.string().trim().min(1).default('/api'),
});
export function parseConsoleEnvironment(environment) {
    const parsed = consoleEnvironmentSchema.parse(environment);
    return { apiBaseUrl: parsed.VITE_API_BASE_URL };
}
//# sourceMappingURL=index.js.map